import React, { useState } from 'react';
import queryString from 'query-string';
import { observer } from 'mobx-react';
import Spinner from '../Spinner';
import './styles.css';
import { RSInput, RSButton, RSCheckbox } from 'reactsymbols-kit';
import MerlinSelect, { type MerlinSelectOption } from '../MerlinSelect';
import { Row, Col } from '../Grid';
import { isEmpty } from 'lodash';
import LoginOrRegisterButtons from './LoginOrRegisterButtons';
import Intro from './Intro';
import logo from './../../images/logo.png';
import TermsAndConditions from './TermsAndConditions';
import Styles from '../../styles/colors';
import { useStores } from '../../hooks/useStores';

import { typeOptions } from '../Account';

// reactsymbols-kit's RSInput/RSCheckbox extend React.HTMLProps, so their declared
// `onChange` is the unusable intersection of the DOM FormEventHandler with the
// kit's value callback. Route the real value handler through this helper, re-typed
// as the shape each component's prop wants — the same library-boundary escape used
// in ModelSettingsForm; the runtime handler is unchanged.
type RSInputChange = React.ComponentProps<typeof RSInput>['onChange'];
type RSCheckboxChange = React.ComponentProps<typeof RSCheckbox>['onChange'];
function asRSChange<T>(handler: (...args: never[]) => void): T {
  return handler as unknown as T;
}

/**
 * Derive the initial wizard state from the `query` prop.
 *
 * By default we are in step 1. If we got redirected here by the backend, the
 * query parameter 'token' will be set, in which case we pre-fill the form from
 * the query and start at step 2. The class did this in its constructor; here it
 * lives in a useState initializer so the very first render already shows the
 * correct step — same single first render, no behavior change.
 */
/** The wizard's merged state, sliced into per-field useState slots below. */
interface RegistrationState {
  step: number;
  firstname: string;
  lastname: string;
  email: string;
  type: string;
  affiliation: string;
  token: string | null;
  terms: boolean;
}

function initialState(query: string): RegistrationState {
  const baseState: RegistrationState = {
    step: 1,
    firstname: '',
    lastname: '',
    email: '',
    type: 'STUDENT',
    affiliation: '',
    token: null,
    terms: false,
  };

  const parsed = queryString.parse(query);
  if (parsed['token'] && parsed['token'].length > 0) {
    // `queryString.parse` types each value as `string | string[] | null`; the
    // backend sets these as single-value params, and the wizard treats them as
    // strings (the `token` truthiness guard above narrows presence). Cast at this
    // query-parse boundary — type-only, runtime values are unchanged.
    return {
      ...baseState,
      firstname: parsed['firstname'] as string,
      lastname: parsed['lastname'] as string,
      email: parsed['email'] as string,
      token: parsed['token'] as string,
      step: 2,
    };
  } else {
    return baseState;
  }
}

/**
 * The registation wizard on /register.
 *
 * This wizards has several steps:
 * 1. The initial step: no identity yet. This renders buttons to pick an
 *    identity provider. This is basically the same as for login.
 * 2. There is an identity, but no user yet. The backend redirects here after
 *    login in with an identity provider (at least the 'token' query parameter
 *    will be set).
 * 3. Details have been confirmed. Show the terms and conditions and ask to
 *    accept these. When the user accepts: do the actual registration with the
 *    backend.
 *
 *
 * For step 2, the backend redirects here after login with an identity provider
 * if there's no user found for the identity used. It sets the following query
 * parameters (if the information is available):
 * - firstname: the first name of the user
 * - lastname: the last name of the user
 * - email: the email address of the user
 * - token: the token with the assertion of the user's identity
 *
 * Props:
 * - query: the query string from the URL (passed by App)
 *
 * The auth store was injected with mobx-react (`inject(({ store }) => ({ auth:
 * store.auth }))`); it now comes from useStores().
 */
interface RegistrationWizardProps {
  /** The query string from the URL (passed by App). */
  query: string;
}

const RegistrationWizard = observer(function RegistrationWizard({ query }: RegistrationWizardProps) {
  const store = useStores();
  const auth = store.auth;

  // Each piece of the wizard state is its own slot. The class used a single
  // merged state object with per-field `setState({ field: value })` partial
  // updates; per-field useState reproduces those exact single-field semantics
  // (useState replaces, so a merged object would need explicit spreading).
  const [init] = useState(() => initialState(query));
  const [step, setStep] = useState(init.step);
  const [firstname, setFirstname] = useState(init.firstname);
  const [lastname, setLastname] = useState(init.lastname);
  const [email, setEmail] = useState(init.email);
  const [type, setType] = useState(init.type);
  const [affiliation, setAffiliation] = useState(init.affiliation);
  const [token] = useState(init.token);
  const [terms, setTerms] = useState(init.terms);

  // Step 2: intercept form submission and go to step 3 if the form is valid.
  const onFinishStep2 = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (step2Valid) {
      setStep(3);
    }
  };

  // Generic form change handler. NOTE: this was already dead code in the class
  // — no field wires to it (every input uses an inline onChange). Carried over
  // verbatim to keep behavior identical.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    const key = target.name;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    // Dynamic dispatch over a map of setters with heterogeneous value types
    // (number/string/boolean), keyed by the field name. The per-setter argument
    // type can't be reconciled statically here; this branch is dead code (no
    // field wires to it) carried over verbatim, so type the lookup loosely.
    const setters: Record<string, (value: never) => void> = {
      step: setStep,
      firstname: setFirstname,
      lastname: setLastname,
      email: setEmail,
      type: setType,
      affiliation: setAffiliation,
      terms: setTerms,
    };
    // `x && x()` short-circuit guard from the legacy code; written as an `if`
    // to satisfy no-unused-expressions. Behaviour-neutral, runtime identical.
    if (setters[key]) setters[key](value as never);
  };

  /**
   * Is the form valid?
   *
   * Account type is a dropdown without empty state, this it cannot be empty.
   */
  const step2Valid = !isEmpty(firstname) && !isEmpty(lastname) && !isEmpty(email);

  /**
   * Finish the registration by submitting the registration to the backend.
   */
  const onFinishStep3 = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (terms) {
      // `token` is `string | null` in state but is always present by step 3
      // (the wizard only reaches step 2/3 once the backend has set the token);
      // `register` takes a `string`. Assert non-null — type-only.
      auth.register(firstname, lastname, email, type, affiliation, token!);
    }
  };

  // Step 1: pick an identity provider
  const renderStep1 = () => {
    return <LoginOrRegisterButtons method="register" />;
  };

  /**
   * Render the registration form
   */
  const renderStep2 = () => {
    return (
      <React.Fragment>
        <h2 className="m-0 w-full text-left text-[38px] font-light tracking-[-2.53px]">
          We're almost there
        </h2>
        <p className="mt-0 mb-[30px] w-full text-[16px] font-light leading-[27px]">
          Can you please check or provide the following information?
        </p>
        <form onSubmit={onFinishStep2}>
          <Row gutter={30}>
            <Col md={6}>
              <RSInput
                label="Firstname"
                value={firstname}
                onChange={asRSChange<RSInputChange>((value: string) => setFirstname(value))}
              />
            </Col>
            <Col md={6}>
              <RSInput
                label="Lastname"
                value={lastname}
                onChange={asRSChange<RSInputChange>((value: string) => setLastname(value))}
              />
            </Col>
            <Col md={12}>
              <RSInput
                label="E-mail"
                value={email}
                onChange={asRSChange<RSInputChange>((value: string) => setEmail(value))}
                type="email"
              />
            </Col>
            <Col md={6}>
              <div className="rs-element rs-input-container rs-input">
                <label className="rs-element rs-form-label">Account Type</label>
                <MerlinSelect
                  className="rs-input-wrap"
                  label="Account type"
                  options={typeOptions}
                  value={type}
                  onChange={(option: MerlinSelectOption | null) =>
                    option != null && setType(option.value as string)
                  }
                  searchable={false}
                  clearable={false}
                />
              </div>
            </Col>
            <Col md={6}>
              <RSInput
                label="Affiliation"
                value={affiliation}
                onChange={asRSChange<RSInputChange>((value: string) => setAffiliation(value))}
              />
            </Col>
          </Row>
          <RSButton
            background={Styles.blueGradient}
            value="Continue registration"
            disabled={!step2Valid}
          />
        </form>
      </React.Fragment>
    );
  };

  // Step 3: the terms and conditions
  const renderStep3 = () => {
    return (
      <React.Fragment>
        <h2 className="m-0 w-full text-left text-[38px] font-light tracking-[-2.53px]">
          Just one more thing...
        </h2>
        <p className="mt-0 mb-[30px] w-full text-[16px] font-light leading-[27px]">
          Please agree to our terms and conditions:
        </p>
        <div className="registration-tandc">
          <TermsAndConditions />
          <form onSubmit={onFinishStep3} className="mt-[30px]">
            <RSCheckbox
              label="I confirm that I have read, understood and agree to these terms and conditions."
              checked={terms}
              onChange={asRSChange<RSCheckboxChange>((value: string, checked: boolean) => setTerms(checked))}
            />
            {auth.registering ? (
              <Spinner />
            ) : (
              <RSButton
                background={Styles.blueGradient}
                value="Complete registration"
                disabled={!terms}
              />
            )}
          </form>
        </div>
      </React.Fragment>
    );
  };

  // Render the correct step
  const renderContent = () => {
    switch (step) {
      case 3:
        return renderStep3();
      case 2:
        return renderStep2();
      case 1:
      default:
        return renderStep1();
    }
  };

  return (
    <div className="register-page flex min-h-screen">
      <div className="register-wrapper flex max-w-[550px] flex-[35%_0_0] flex-col items-center justify-center p-10">
        <img src={logo} alt="Logo" className="logo w-[240px] mb-[130px]" />
        {renderContent()}
      </div>
      <Intro className="register-intro flex flex-1 flex-col items-center justify-center bg-[linear-gradient(135deg,#51adf6_0%,#2875ea_100%)] px-[100px] text-left text-[#f8fafb]" />
    </div>
  );
});

export default RegistrationWizard;
